/**
 * 数字展馆服务层（/api/v2/showcase）
 * 数字展馆技术标准 §4/§5：v2 对外契约、复用 v1 拦截器（JWT 自动注入）
 */
import instance from './api'

/** v2 基址：将 v1 基址的版本段替换为 v2 */
const V2_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/v\d+$/, '/v2')

export interface Exhibit {
  _id: string
  sid: string
  ownerId: string
  brandId: string
  productId: string
  orderNo: string
  editionNo: number
  editionTotal: number
  productName: string
  productImageUrl?: string
  status: string
  placedSpaceId?: string | null
  createdAt: string
}

export interface SpaceItemView {
  id: string
  exhibitId?: string
  exhibitSid?: string | null
  productName?: string | null
  productImageUrl?: string | null
  /** 3D 模型 URL（Phase U GLB 渲染；缺失时降级贴图卡片） */
  glbUrl?: string | null
  editionNo?: number | null
  editionTotal?: number | null
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  scale: { x: number; y: number; z: number }
}

export interface SpaceEventInfo {
  enabled: boolean
  title?: string
  productId?: string
  startAt: string
  endAt: string
}

export interface SpaceDetail {
  _id: string
  sid: string
  name: string
  ownerId: string
  ownerType: string
  brandId?: string
  isDefault: boolean
  template: string
  isPublic: boolean
  visitCount: number
  items: SpaceItemView[]
  /** U2.2 发布会活动（仅时间窗内返回） */
  event?: SpaceEventInfo | null
  createdAt: string
}

export interface CreateSpaceInput {
  name: string
  template?: string
  isPublic?: boolean
}

const v2 = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    instance.get<T>(url, { params, baseURL: V2_BASE_URL }) as Promise<T>,
  post: <T>(url: string, data?: unknown) =>
    instance.post<T>(url, data, { baseURL: V2_BASE_URL }) as Promise<T>,
  patch: <T>(url: string, data?: unknown) =>
    instance.patch<T>(url, data, { baseURL: V2_BASE_URL }) as Promise<T>,
  delete: <T>(url: string) =>
    instance.delete<T>(url, { baseURL: V2_BASE_URL }) as Promise<T>,
}

export const showcaseService = {
  // ─── 展品 ───
  getMyExhibits: () => v2.get<Exhibit[]>('/showcase/exhibits/my'),
  getExhibit: (sid: string) => v2.get<Exhibit>(`/showcase/exhibits/${sid}`),

  // ─── 展馆 ───
  getMySpaces: () => v2.get<SpaceDetail[]>('/showcase/spaces/my'),
  createSpace: (input: CreateSpaceInput) => v2.post<SpaceDetail>('/showcase/spaces', input),
  getSpaceDetail: (id: string) => v2.get<SpaceDetail>(`/showcase/spaces/${id}`),
  updateSpace: (id: string, patch: Partial<CreateSpaceInput>) =>
    v2.patch<SpaceDetail>(`/showcase/spaces/${id}`, patch),
  deleteSpace: (id: string) => v2.delete<{ success: boolean }>(`/showcase/spaces/${id}`),

  // ─── 陈列位 ───
  addItem: (spaceId: string, exhibitId: string, position?: { x: number; y: number; z: number }) =>
    v2.post(`/showcase/spaces/${spaceId}/items`, { exhibitId, position }),
  removeItem: (spaceId: string, itemId: string) =>
    v2.delete(`/showcase/spaces/${spaceId}/items/${itemId}`),
  updateItem: (
    spaceId: string,
    itemId: string,
    patch: Partial<Pick<SpaceItemView, 'position' | 'rotation' | 'scale'>>,
  ) => v2.patch(`/showcase/spaces/${spaceId}/items/${itemId}`, patch),

  // ─── 公开广场 ───
  getPublicSpaces: (cursor?: string, limit = 20) =>
    v2.get<{ items: SpaceDetail[]; nextCursor: string | null }>('/showcase/spaces/public', {
      cursor,
      limit,
    }),
  getBrandHalls: (brandId: string) => v2.get<SpaceDetail[]>(`/showcase/spaces/brand/${brandId}`),
  visitSpace: (id: string) => v2.post(`/showcase/spaces/${id}/visit`),
  like: (id: string) => v2.post(`/showcase/spaces/${id}/like`),
  unlike: (id: string) => v2.delete(`/showcase/spaces/${id}/like`),
  isLiked: (id: string) => v2.get(`/showcase/spaces/${id}/like`),
  getVisitors: (id: string) => v2.get(`/showcase/spaces/${id}/visitors`),
  getWorldHalls: () => v2.get('/showcase/spaces/world/halls'),
}
